import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ShieldOff } from 'lucide-react';

import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { formatDate } from '../../../utils/Utils';
import { SectionTitle, Rows, Label, Value } from '../CandidateProfileSections';
import { renewConsent, anonymizeCandidate } from '../config/profileApi';

// KVKK / RIZA — rıza sürümü, bitişi, saklama vadesi ve anonimleştirme durumu.
//
// "Rızayı Yenile" AYRI uçtur (`PUT {id}/consent`): aday formundaki `Update`
// ConsentExpiresAt'i null'a ezme tuzağı taşır, rıza oradan ASLA yazılmaz. Sunucu her
// yenilemeyi ayrıca bir sistem notu olarak iz bırakır (kanıt notu dahil).
//
// "Şimdi Anonimleştir" GERİ ALINAMAZ: onay diyaloğu bir "anlıyorum" onayı olmadan
// kilitli kalır. Aktif başvurusu olan adayda sunucu 409
// `ats.candidate.anonymizeBlockedByActiveProcess` döner (mesajı interceptor gösterir).

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 6px;
  padding: 20px 24px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  max-width: 720px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ $ok, theme }) => ($ok ? theme.colors.auxiliar.success : theme.colors.auxiliar.danger)};
  background: ${({ $ok, theme }) => ($ok ? theme.colors.auxiliar.success : theme.colors.auxiliar.danger)}1A;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const Hint = styled.p`
  margin: 12px 0 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Rıza "geçerli" = bitiş tarihi gelecekte. Tarihsiz rıza geçerli SAYILMAZ (bilinmiyor).
export const isConsentValid = (expiresAt, now = new Date()) => {
  if (!expiresAt) return false;
  const d = new Date(expiresAt);
  return !Number.isNaN(d.getTime()) && d > now;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function CandidateConsentTab({
  candidateId, profile, writable, onChanged,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const consent = profile?.consent || {};
  const version = consent.version ?? profile?.kvkkConsentVersion ?? null;
  const expiresAt = consent.expiresAt ?? profile?.consentExpiresAt ?? null;
  const valid = isConsentValid(expiresAt);
  const d = (v) => (v ? formatDate(v, lang) : '-');

  const [renewOpen, setRenewOpen] = useState(false);
  const [anonymizeOpen, setAnonymizeOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const renewSchema = useMemo(() => [
    {
      name: 'expiresAt',
      label: t('ats.candidateProfile.consentNewExpiry'),
      type: 'date',
      grid: 6,
      required: true,
      minDate: todayIso(),
    },
    {
      name: 'consentVersion',
      label: t('ats.candidateProfile.consentVersion'),
      type: 'number',
      grid: 6,
      min: 1,
      description: t('ats.candidateProfile.consentVersionHint'),
    },
    {
      name: 'evidenceNote',
      label: t('ats.candidateProfile.consentEvidence'),
      type: 'textarea',
      rows: 4,
      grid: 12,
      required: true,
      description: t('ats.candidateProfile.consentEvidenceHint'),
    },
  ], [t]);

  const renewInitial = useMemo(
    () => (renewOpen ? { consentVersion: version ?? '', expiresAt: '', evidenceNote: '' } : null),
    [renewOpen, version],
  );

  const handleRenew = useCallback(async (values) => {
    const res = await renewConsent(candidateId, values);
    if (!res.ok) return false;
    setRenewOpen(false);
    if (onChanged) onChanged();
    return true;
  }, [candidateId, onChanged]);

  const closeAnonymize = useCallback(() => {
    setAnonymizeOpen(false);
    setAcknowledged(false);
  }, []);

  const handleAnonymize = useCallback(async () => {
    if (!acknowledged) return;
    setBusy(true);
    const res = await anonymizeCandidate(candidateId);
    setBusy(false);
    // Sunucu reddederse (409 aktif başvuru) diyalog kapanır, mesajı interceptor gösterir.
    closeAnonymize();
    if (res.ok && onChanged) onChanged();
  }, [acknowledged, candidateId, closeAnonymize, onChanged]);

  const anonymized = !!profile?.anonymizedAt;

  return (
    <Card data-role="candidate-consent">
      <SectionTitle>{t('ats.candidateProfile.consentSection')}</SectionTitle>
      <Rows>
        <Label>{t('ats.candidateProfile.consentStatus')}</Label>
        <Value>
          <StatusPill $ok={valid} data-role="candidate-consent-status" data-valid={valid ? 'true' : 'false'}>
            {valid ? t('ats.candidateProfile.consentValid') : t('ats.candidateProfile.consentExpired')}
          </StatusPill>
        </Value>
        <Label>{t('ats.candidateProfile.consentVersion')}</Label>
        <Value>{version ?? '-'}</Value>
        <Label>{t('ats.candidateProfile.consentExpiresAt')}</Label>
        <Value>{d(expiresAt)}</Value>
        <Label>{t('ats.candidateProfile.retentionDueAt')}</Label>
        <Value>{d(consent.retentionDueAt)}</Value>
        <Label>{t('ats.candidateProfile.anonymizationPolicy')}</Label>
        <Value>
          {consent.anonymizationEnabled
            ? t('ats.candidateProfile.anonymizationAuto')
            : t('ats.candidateProfile.anonymizationManual')}
        </Value>
        <Label>{t('ats.candidateProfile.anonymizedAt')}</Label>
        <Value>{d(profile?.anonymizedAt)}</Value>
      </Rows>

      {writable && !anonymized && (
        <Actions>
          <Button type="button" data-role="candidate-consent-renew" onClick={() => setRenewOpen(true)}>
            <RefreshCw size={14} />
            {t('ats.candidateProfile.renewConsent')}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-role="candidate-anonymize"
            onClick={() => setAnonymizeOpen(true)}
          >
            <ShieldOff size={14} />
            {t('ats.candidateProfile.anonymizeNow')}
          </Button>
        </Actions>
      )}
      <Hint>{t('ats.candidateProfile.consentHint')}</Hint>

      <GenericFormDrawer
        open={renewOpen}
        onClose={() => setRenewOpen(false)}
        title={t('ats.candidateProfile.renewConsent')}
        schema={renewSchema}
        initialValues={renewInitial}
        onSave={handleRenew}
      />

      <ConfirmDialog
        isOpen={anonymizeOpen}
        title={t('ats.candidateProfile.anonymizeNow')}
        message={t('ats.candidateProfile.anonymizeConfirm', { name: profile?.fullName || '' })}
        onConfirm={handleAnonymize}
        onCancel={closeAnonymize}
        confirmDisabled={!acknowledged}
        loading={busy}
        type="danger"
      >
        <div data-role="candidate-anonymize-ack">
          <CheckboxInput
            label={t('ats.candidateProfile.anonymizeAcknowledge')}
            checked={acknowledged}
            onChange={setAcknowledged}
          />
        </div>
      </ConfirmDialog>
    </Card>
  );
}
